"use client";

import { Card, CardContent } from "@/components/ui/card";
import { IoCheckmarkCircle } from "react-icons/io5";

import { CustomTooltip } from "@/components/common/customTooltip";
import { Checkbox } from "@/components/ui/checkbox";
import { importInitialState, useImport } from "@/context/importContext";
import { useSession } from "@/context/SessionContext";
import { useToast } from "@/context/ToastContext";
import { ftInToCm } from "@/utils/helpers/conversion";
import {
  useImportCandidates,
  useImportContracts,
  useImportStatic,
} from "@/utils/hooks/import/importHooks";
import { usePreviewCandidateWarnings } from "@/utils/hooks/import/importProcessingHooks";
import { StaticDataType } from "@/utils/hooks/static/staticHooks";
import {
  candidateIdentityRows,
  importWarningText,
  isForceableWarning,
} from "@/utils/import/candidatesImport";
import { importRefusalMessage } from "@/utils/import/importUsage";
import { clearPendingImport } from "@/utils/import/pendingImport";
import ImportLimitNotice from "./ImportLimitNotice";
import ImportStopNotice from "./ImportStopNotice";
import { ImportProfileType } from "@/utils/import/profiles/importProfiles";
import { booleanTranslation } from "@/utils/import/validations/columnValidations";
import { createValidationService } from "@/utils/import/validations/validationService";
import {
  ImportCandidatesDataType,
  importMatchCountryStateAction,
} from "@/utils/serverActions/import/importActions";
import EditableImportCell from "./EditableImportCell";
import React, { useState } from "react";
import { LuArrowLeft, LuTriangleAlert } from "react-icons/lu";
import { Button } from "../ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";

/** Warning icon that lists validation errors (red) or warnings (amber) on hover or focus. */
function ErrorTooltip({
  title,
  children,
  tone = "error",
}: {
  title: string;
  children: React.ReactNode;
  tone?: "error" | "warning";
}) {
  return (
    <CustomTooltip
      trigger={
        <button type="button" aria-label={title} className="shrink-0 cursor-help">
          <LuTriangleAlert
            className={`h-4 w-4 ${tone === "error" ? "text-red-500" : "text-amber-500"}`}
          />
        </button>
      }
      contentClassName={
        tone === "error"
          ? "border border-de bg-de-4 text-de"
          : "border border-amber-300 bg-amber-50 text-amber-900"
      }
    >
      <div className="space-y-1">
        <p className="font-semibold text-sm">{title}</p>
        {children}
      </div>
    </CustomTooltip>
  );
}

// interface ColumnInfo {
//   required: boolean;
//   conditionalRequired?: boolean;
//   sampleData: string[];
//   customError?: string;
//   type: string;
// }

interface ColumnGroup {
  title: string;
  columns: Array<{
    header: string;
    key: string;
    dataKey: string;
    index: number;
  }>;
  color: string;
}
// import { FileLeverErrorType } from "@/utils/import/validations/columnValidations";

export default function PreviewTable() {
  const { importData, setImportData } = useImport();
  const { rowErrors, rowWarnings, forcedRows, selectedProfile, previewData, importResults, usage } =
    importData;
  const previewCandidateWarnings = usePreviewCandidateWarnings();

  // Debug: Log when importData changes
  React.useEffect(() => {
    console.log("🔄 ImportData context changed:", importData);
  }, [importData]);

  // Debug: Log component mount/unmount
  React.useEffect(() => {
    console.log("🔄 PreviewTable component mounted");
    return () => {
      console.log("🔄 PreviewTable component unmounted");
    };
  }, []);
  const { showToast } = useToast();
  const { session } = useSession();
  const { languageCode, companyId } = session;

  // Estado para el filtro
  const [showOnlyErrors, setShowOnlyErrors] = useState(false);
  // Candidates: the company vouches for the e-mails of the file (no activation code asked).
  const [trustEmails, setTrustEmails] = useState(false);

  // Estado para controlar qué pantalla mostrar
  const [currentScreen, setCurrentScreen] = useState<"validation" | "results">(
    "validation",
  );

  // Debug: Log when importResults changes
  React.useEffect(() => {
    console.log("🔄 Import results state changed:", importResults);
    if (importResults) {
      console.log("📊 Results details:", {
        total: importResults.total_processed,
        successful: importResults.successful_imports,
        duplicates: importResults.duplicates_found,
        errors: importResults.errors_found,
      });
      // Cambiar automáticamente a la pantalla de resultados
      setCurrentScreen("results");
    } else {
      console.log("❌ Import results was reset to null");
    }
  }, [importResults]);

  // The import runs in chunks; the overlay says how far it went.
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const onProgress = (done: number, total: number) => setProgress({ done, total });
  const { mutateAsync: importCandidates } = useImportCandidates({ onProgress });
  const { mutateAsync: importContracts, isPending: isImportingContracts } =
    useImportContracts({ onProgress });
  const importStatic = useImportStatic(languageCode);
  const { ratePeriod, invoiceTimeMode, invoiceMode, invoiceFrequency, gender } =
    importStatic;

  // Función para obtener color de fondo del grupo
  const getGroupBackgroundColor = (key: string) => {
    switch (key) {
      case "contract":
        return "#dbeafe"; // bg-blue-100
      case "client":
        return "#dcfce7"; // bg-green-100
      case "patient1":
        return "#f3e8ff"; // bg-purple-100
      case "patient2":
        return "#fed7aa"; // bg-orange-100
      default:
        return "#f3f4f6"; // bg-gray-100
    }
  };

  // Función para simplificar nombres de columnas - IGUAL QUE PASTEDATAINPUT
  const simplifyColumnName = (header: string, groupTitle: string) => {
    const groupLower = groupTitle.toLowerCase();
    let simplified = header;

    if (groupLower.includes("cliente")) {
      simplified = header
        .replace(/del cliente|de cliente|cliente/gi, "")
        .trim();
    } else if (groupLower.includes("paciente 1")) {
      simplified = header
        .replace(/del paciente 1|de paciente 1|paciente 1/gi, "")
        .trim();
    } else if (groupLower.includes("paciente 2")) {
      simplified = header
        .replace(/del paciente 2|de paciente 2|paciente 2/gi, "")
        .trim();
    } else if (groupLower.includes("contrato")) {
      simplified = header
        .replace(/del contrato|de contrato|contrato/gi, "")
        .trim();
    }

    return simplified || header;
  };

  // Función de agrupación - IGUAL QUE PASTEDATAINPUT
  const groupColumnsByProfileOrder = (headers: string[]) => {
    const groups: Record<string, ColumnGroup> = {
      contract: { title: "📋 Contrato", columns: [], color: "bg-blue-100" },
      client: { title: "👤 Cliente", columns: [], color: "bg-green-100" },
      patient1: { title: "🏥 Paciente 1", columns: [], color: "bg-purple-100" },
      patient2: { title: "🏥 Paciente 2", columns: [], color: "bg-orange-100" },
      other: { title: "📝 Otros", columns: [], color: "bg-gray-100" },
    };

    // Obtener las claves del perfil en orden
    const profileKeys = selectedProfile
      ? Object.keys(selectedProfile.columns)
      : [];

    headers.forEach((header, index) => {
      // Buscar la clave correspondiente en el perfil
      const matchingKey = profileKeys.find((key) => {
        const profileColumn = selectedProfile?.columns[key];
        return (
          profileColumn?.fieldHeader?.[languageCode] === header ||
          profileColumn?.fieldHeader?.en === header
        );
      });

      if (matchingKey) {
        if (matchingKey.startsWith("contract_")) {
          groups.contract.columns.push({
            header: simplifyColumnName(header, groups.contract.title),
            key: matchingKey,
            dataKey: header,
            index,
          });
        } else if (matchingKey.startsWith("client_")) {
          groups.client.columns.push({
            header: simplifyColumnName(header, groups.client.title),
            key: matchingKey,
            dataKey: header,
            index,
          });
        } else if (matchingKey.startsWith("patient1_")) {
          groups.patient1.columns.push({
            header: simplifyColumnName(header, groups.patient1.title),
            key: matchingKey,
            dataKey: header,
            index,
          });
        } else if (matchingKey.startsWith("patient2_")) {
          groups.patient2.columns.push({
            header: simplifyColumnName(header, groups.patient2.title),
            key: matchingKey,
            dataKey: header,
            index,
          });
        } else {
          groups.other.columns.push({
            header: simplifyColumnName(header, groups.other.title),
            key: matchingKey,
            dataKey: header,
            index,
          });
        }
      } else {
        // Fallback a la detección por palabras clave si no se encuentra en el perfil
        const headerLower = header.toLowerCase();
        if (
          headerLower.includes("contrato") ||
          headerLower.includes("contract") ||
          headerLower.includes("tarifa") ||
          headerLower.includes("frecuencia") ||
          headerLower.includes("tiempo")
        ) {
          groups.contract.columns.push({
            header: simplifyColumnName(header, groups.contract.title),
            key: header,
            dataKey: header,
            index,
          });
        } else if (
          headerLower.includes("cliente") ||
          headerLower.includes("client") ||
          headerLower.includes("empresa")
        ) {
          groups.client.columns.push({
            header: simplifyColumnName(header, groups.client.title),
            key: header,
            dataKey: header,
            index,
          });
        } else if (
          headerLower.includes("paciente 1") ||
          headerLower.includes("patient1")
        ) {
          groups.patient1.columns.push({
            header: simplifyColumnName(header, groups.patient1.title),
            key: header,
            dataKey: header,
            index,
          });
        } else if (
          headerLower.includes("paciente 2") ||
          headerLower.includes("patient2")
        ) {
          groups.patient2.columns.push({
            header: simplifyColumnName(header, groups.patient2.title),
            key: header,
            dataKey: header,
            index,
          });
        } else {
          groups.other.columns.push({
            header: simplifyColumnName(header, groups.other.title),
            key: header,
            dataKey: header,
            index,
          });
        }
      }
    });

    // Filtrar grupos vacíos
    return Object.fromEntries(
      Object.entries(groups).filter(([, group]) => group.columns.length > 0),
    );
  };

  // console.log(
  //   "%c props",
  //   "background-color: blue; color: white; font-weight: bold;",
  //   data
  // );

  if (previewData.length === 0) return null;

  // USAR EXACTAMENTE LA MISMA LÓGICA QUE FILEUPLOADER
  const headers = selectedProfile?.columns
    ? Object.keys(selectedProfile.columns).map((key) => {
        const col = selectedProfile.columns[key];
        return col.fieldHeader?.[languageCode] || col.fieldHeader?.en || "";
      })
    : [];

  const columnGroups = groupColumnsByProfileOrder(headers);

  // Debug: verificar estructura de datos y errores
  console.log("🔍 Debug PreviewTable:", {
    headers,
    previewDataKeys: previewData.length > 0 ? Object.keys(previewData[0]) : [],
    rowErrors: rowErrors, // TODOS los errores
    selectedProfileKeys: selectedProfile
      ? Object.keys(selectedProfile.columns)
      : [],
  });

  // Debug específico de errores
  console.log("🚨 TODOS LOS ERRORES:", rowErrors);

  const hasErrors = rowErrors.length > 0;
  // The plan has less left than this import needs: it cannot run.
  const overLimit = usage?.allowed === false;
  const isImportingData = isImportingContracts || importData.isImporting;

  // Calcular indicadores generales
  const totalRows = previewData.length;
  const rowsWithErrors = new Set(rowErrors.map((error) => error.row));
  const validRows = totalRows - rowsWithErrors.size;
  const errorRows = rowsWithErrors.size;

  // Rows to show: the real position in the data is kept, so a row keeps its number and
  // its errors when the list is filtered.
  const visibleRows = previewData
    .map((row, index) => ({ row, index }))
    .filter(({ index }) => !showOnlyErrors || rowsWithErrors.has(index + 1))
    .slice(0, 50);

  const errorFor = (index: number, header: string) =>
    rowErrors.find((error) => error.row === index + 1 && error.field === header);

  const generalErrorsFor = (index: number) =>
    rowErrors.filter((error) => error.row === index + 1 && error.field === "General");

  const warningsFor = (index: number) =>
    rowWarnings.filter((warning) => warning.row_index === index + 1);

  const toggleForced = (index: number, checked: boolean) =>
    setImportData((prev) => ({
      ...prev,
      forcedRows: checked
        ? [...prev.forcedRows.filter((row) => row !== index + 1), index + 1]
        : prev.forcedRows.filter((row) => row !== index + 1),
    }));

  // Fields that only accept some values are corrected from a list of them.
  const optionsFor = (key: string): string[] | undefined => {
    const type = selectedProfile.columns[key]?.type;
    const labels = (items: StaticDataType[]) =>
      items.map((item) => item.label).filter((label): label is string => Boolean(label));
    if (type === "gender") return labels(gender.data.filter((item) => item.value !== 3));
    if (key === "contract_rate_period") return labels(ratePeriod.data);
    if (key === "contract_invoice_frequency") return labels(invoiceFrequency.data);
    if (key === "contract_invoice_time_mode") return labels(invoiceTimeMode.data);
    if (key === "contract_invoice_mode") return labels(invoiceMode.data);
    return undefined;
  };

  // A corrected cell: the row is validated again with the same rules as when it was
  // read, so its errors (and the totals) are the ones of the new value.
  const commitCell = async (index: number, key: string, header: string, value: string) => {
    try {
      let countryStateData = importData.countryStateData;
      if (key === "country" && value !== "") {
        const found = await importMatchCountryStateAction({ p_country_names: [value] });
        countryStateData = [...(countryStateData ?? []), ...(found ?? [])];
      }

      const cellValue =
        selectedProfile.columns[key]?.type === "email" ? value.trim().toLowerCase() : value;
      const nextRow = { ...(previewData[index] as Record<string, unknown>), [header]: cellValue };
      const service = createValidationService(
        selectedProfile.columns,
        languageCode,
        countryStateData,
        importStatic,
        selectedProfile.atLeastOneOf,
      );
      const result = service.validateRow(nextRow);
      const nextErrors = Object.entries(result.errors).map(([field, error]) => ({
        row: index + 1,
        field,
        error,
      }));

      setImportData((prev) => {
        const rowErrors = [
          ...prev.rowErrors.filter((error) => error.row !== index + 1),
          ...nextErrors,
        ];
        return {
          ...prev,
          previewData: prev.previewData.map((row, rowIndex) =>
            rowIndex === index ? nextRow : row,
          ),
          rowErrors,
          countryStateData,
          totalRecords: prev.previewData.length - new Set(rowErrors.map((error) => error.row)).size,
        };
      });

      // A corrected name, e-mail or national id can change what the database says
      // about this row and the others (duplicates inside the file).
      // The database only needs to look again when what identifies the person changed
      // (name, e-mail, national id): the warnings do not depend on the other cells.
      const identityOf = (row: Record<string, unknown>) =>
        JSON.stringify(candidateIdentityRows([row], selectedProfile, languageCode)[0]);
      if (
        selectedProfile.id === "candidates" &&
        identityOf(previewData[index] as Record<string, unknown>) !== identityOf(nextRow)
      ) {
        const nextRows = previewData.map((row, rowIndex) =>
          rowIndex === index ? nextRow : (row as Record<string, unknown>),
        );
        const nextWarnings = await previewCandidateWarnings(nextRows);
        setImportData((prev) => ({
          ...prev,
          rowWarnings: nextWarnings,
          // An accepted warning that no longer exists is not accepted any more.
          forcedRows: prev.forcedRows.filter((row) =>
            nextWarnings.some(
              (warning) => warning.row_index === row && isForceableWarning(warning.warning),
            ),
          ),
        }));
      }
    } catch (error) {
      console.error("Error validating the corrected value:", error);
      showToast({
        description: "No se pudo validar el valor corregido. Inténtalo de nuevo.",
        variant: "destructive",
      });
    }
  };

  const handleImport = async () => {
    try {
      setImportData((prev) => ({ ...prev, isImporting: true }));
      setProgress({ done: 0, total: previewData.length });

      // Convert the data to use the column keys from the profile instead of the field header
      // which changes depending on the language
      const dataWithKeys = <T extends Record<string, unknown>>(
        data: Record<string, unknown>[],
        profile: ImportProfileType,
        languageCode: string,
      ): T[] => {
        return data.map((row) => {
          const newRow: Partial<T> = {};

          Object.entries(row).forEach(([header, value]) => {
            const column = Object.entries(profile.columns).find(
              ([, col]) => col.fieldHeader?.[languageCode] === header,
            );

            if (column) {
              const key = column[0] as keyof T;
              newRow[key] = value as T[keyof T];
            }
          });

          return newRow as T;
        });
      };

      console.log(
        "%c dataWithKeys",
        "background-color: blue; color: white; font-weight: bold;",
        dataWithKeys(previewData, selectedProfile, languageCode),
      );

      type KeyMappingType =
        | "contract_rate_period"
        | "contract_invoice_frequency"
        | "contract_invoice_time_mode"
        | "contract_invoice_mode";

      const keyMapping: Record<KeyMappingType, StaticDataType[]> = {
        contract_rate_period: ratePeriod.data,
        contract_invoice_frequency: invoiceFrequency.data,
        contract_invoice_time_mode: invoiceTimeMode.data,
        contract_invoice_mode: invoiceMode.data,
      };

      console.log("🔄 Starting data transformation...");
      console.log("📊 Preview data length:", previewData.length);
      console.log("📋 First row sample:", previewData[0]);
      console.log("🎯 Selected profile:", selectedProfile?.name);

      const transformedData = dataWithKeys<Record<string, unknown>>(
        previewData as Record<string, unknown>[],
        selectedProfile,
        languageCode,
      ).map((row) => {
        return Object.entries(row as Record<string, unknown>).reduce(
          (acc, [key, value]) => {
            const columnType = selectedProfile?.columns[key]?.type;

            // convert some columns from descriptions to corresponding id and change the column name

            if (keyMapping[key as KeyMappingType]) {
              acc[`${key}_id`] =
                keyMapping[key as KeyMappingType]?.find(
                  (item) => item.label === value,
                )?.value || 0;
            } else if (columnType === "boolean") {
              acc[key] =
                booleanTranslation.en[0] === value ||
                booleanTranslation.es[0] === value;
            } else if (columnType === "phone") {
              // Clean phone numbers by removing all spaces
              acc[key] = (value as string)?.replace(/\s/g, "") || null;
            } else if (columnType === "gender") {
              // Use dynamic gender data instead of hardcoded values
              const genderMatch = gender.data.find(
                (genderItem) =>
                  genderItem.label?.toLowerCase() ===
                    (value as string)?.toLowerCase() ||
                  genderItem.label?.toLowerCase() ===
                    (value as string)
                      ?.toLowerCase()
                      .replace(/[áéíóú]/g, (match) => {
                        const accents: Record<string, string> = {
                          á: "a",
                          é: "e",
                          í: "i",
                          ó: "o",
                          ú: "u",
                        };
                        return accents[match] || match;
                      }),
              );
              // Candidates: an empty gender stays empty (the person fills it in).
              // Other profiles keep the old default of 1.
              acc[key] =
                genderMatch?.value ||
                (importData.importType === "candidates" ? null : 1);
            } else if (
              key === "patient1_height_system" ||
              key === "patient2_height_system"
            ) {
              acc[`${key}`] = value === "cm" ? 1 : 2;
            } else if (
              key === "patient1_weight_system" ||
              key === "patient2_weight_system"
            ) {
              acc[`${key}`] = value === "kg" ? 1 : 2;
            } else if (
              (key === "patient1_height" &&
                (row as Record<string, unknown>)["patient1_height_system"] ===
                  "ft") ||
              (key === "patient2_height" &&
                (row as Record<string, unknown>)["patient2_height_system"] ===
                  "ft")
            ) {
              const feet = Math.floor(value as number);
              const inches = Math.round(((value as number) - feet) * 10);
              acc[key] = ftInToCm(feet, inches);
            } else if (columnType === "date" && typeof value === "string") {
              // Convertir fechas de DD/MM/YYYY a formato ISO para PostgreSQL
              const dateParts = (value as string).split("/");
              if (dateParts.length === 3) {
                const [day, month, year] = dateParts;
                const isoDate = `${year}-${month.padStart(
                  2,
                  "0",
                )}-${day.padStart(2, "0")}`;
                acc[key] = isoDate;
              } else {
                acc[key] = value;
              }
            } else {
              acc[key] = value;
            }

            return acc;
          },
          {} as Record<string, unknown>,
        );
      });

      console.log("✅ Data transformation completed");
      console.log("📊 Transformed data length:", transformedData.length);
      console.log("📋 First transformed row:", transformedData[0]);
      console.log("🎯 Import type:", importData.importType);

      // Finally, call the appropriate import function based on type
      switch (importData.importType) {
        case "candidates":
          try {
            const candidatesResult = await importCandidates({
              p_action: "import",
              // The profile names the column "gender"; the database reads gender_id.
              // force: the user accepted the warning of the row.
              p_data: transformedData.map((row, rowIndex) => ({
                ...(row as ImportCandidatesDataType),
                gender_id: (row.gender as number | null | undefined) ?? null,
                force: forcedRows.includes(rowIndex + 1),
                trusted: trustEmails,
              })),
              p_company_id: companyId,
              p_created_by: 4, // 4=import
            });
            // Nothing ran: the screen explains why. Stopped in the middle: the results show what ran.
            if (candidatesResult.refused && candidatesResult.total_processed === 0) {
              showToast({
                description: importRefusalMessage(candidatesResult.refused, candidatesResult.usage, candidatesResult.block, session.usesHour12),
                variant: "destructive",
              });
            } else {
              setImportData((prev) => ({ ...prev, candidatesResult }));
              // What waited for an answer of Orquesti is done.
              void clearPendingImport(companyId);
            }
          } catch {
            // The hook already shows the error; the rows stay on screen to try again.
          }
          break;
        case "contracts":
          try {
            console.log("🚀 Starting contracts import...");
            console.log("📊 Transformed data:", transformedData);
            console.log("🏢 Company ID:", companyId);
            console.log("🌐 Language code:", languageCode);

            const result = await importContracts({
              p_data: transformedData,
              p_company_id: companyId,
              p_language_code: languageCode,
            });

            console.log("✅ Import result:", result);
            console.log("📈 Result type:", typeof result);
            console.log(
              "📋 Result keys:",
              result ? Object.keys(result) : "No result",
            );

            // Show results in the same dialog (nothing was written when it was refused)
            if (result.refused && result.total_processed === 0) {
              showToast({
                description: importRefusalMessage(result.refused, result.usage, result.block, session.usesHour12),
                variant: "destructive",
              });
              break;
            }
            console.log("🎯 Setting results in state...");
            setImportData((prev) => ({ ...prev, importResults: result }));
            void clearPendingImport(companyId);
            console.log("🎯 Results set in state");

            // Verify the state was set correctly
            setTimeout(() => {
              console.log("🔍 State after timeout:", importResults);
            }, 100);
          } catch (error) {
            console.error("❌ Error during import:", error);
            showToast({
              description:
                "Error durante la importación: " + (error as Error).message,
              variant: "destructive",
            });
          }
          break;
        // Add other cases as needed
      }

      // Move your import logic here
      // This should include any API calls, data processing, etc.
      // that was previously handled in the parent component

      // Example:
      // await importData(parsedData);
      // toast.success("Importación completada");
      // setImportData((prev) => ({ ...prev, open: false }));
    } catch (error) {
      console.error(error);
      showToast({
        title: "Error al importar los datos",
        variant: "destructive",
      });
    } finally {
      setProgress(null);
      setImportData((prev) => ({ ...prev, isImporting: false }));
    }
  };

  const formatCellValue = (value: unknown, header: string): string => {
    // Find the column by matching the fieldHeader
    const column = Object.entries(selectedProfile?.columns || {}).find(
      ([, col]) => col.fieldHeader?.[languageCode] === header,
    );

    let formattedValue = String(value || "");

    if (column?.[1].type === "date" && typeof value === "string") {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        formattedValue = date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      }
    }

    // Truncar texto si excede 30 caracteres (aproximadamente el ancho de la columna)
    if (formattedValue.length > 30) {
      return formattedValue.substring(0, 30) + "...";
    }

    return formattedValue;
  };

  // Función para renderizar la pantalla de validación
  const renderValidationScreen = () => (
    <div className="relative">
      <div
        className={`space-y-6 ${
          isImportingData ? "pointer-events-none opacity-75" : ""
        }`}
      >
        <ImportLimitNotice />

        {/* Indicadores generales */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-800">
                    Total Filas
                  </p>
                  <p className="text-2xl font-bold text-blue-900">
                    {totalRows}
                  </p>
                </div>
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <IoCheckmarkCircle className="h-5 w-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-800">
                    Datos Válidos
                  </p>
                  <p className="text-2xl font-bold text-green-900">
                    {validRows}
                  </p>
                </div>
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <IoCheckmarkCircle className="h-5 w-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-red-800">
                    Con Errores
                  </p>
                  <p className="text-2xl font-bold text-red-900">{errorRows}</p>
                </div>
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                  <LuTriangleAlert className="h-5 w-5 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-gray-200 bg-gray-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-800">
                    Porcentaje
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {totalRows > 0
                      ? Math.round((validRows / totalRows) * 100)
                      : 0}
                    %
                  </p>
                </div>
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-bold text-gray-600">%</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filtro de errores */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <Button
              variant={showOnlyErrors ? "default" : "outline"}
              size="sm"
              onClick={() => setShowOnlyErrors(!showOnlyErrors)}
              className="flex items-center gap-2"
            >
              <LuTriangleAlert className="h-4 w-4" />
              {showOnlyErrors ? "Mostrar Todas" : "Solo Errores"}
            </Button>
            <span className="text-sm text-gray-600">
              {showOnlyErrors
                ? `${errorRows} filas con errores`
                : `${totalRows} filas totales`}
            </span>
          </div>
        </div>

        {/* Preview Table */}
        <Card className="border-bg+2">
          <CardContent className="p-0">
            <div className="w-full max-w-full h-[calc(100dvh-600px)] overflow-auto">
              <Table
                className="w-full min-w-max"
                heightClasses="h-[calc(100dvh-500px)]"
              >
                <TableHeader>
                  {/* Fila de grupos: solo cuando hay más de un grupo (contratos) */}
                  {Object.keys(columnGroups).length > 1 && (
                    <TableRow>
                      <TableHead className="text-xs whitespace-nowrap min-w-16 font-medium text-center"></TableHead>
                      {Object.entries(columnGroups).map(
                        ([groupKey, group], groupIndex) => {
                          if (group.columns.length === 0) return null;

                          return (
                            <TableHead
                              key={groupKey}
                              colSpan={group.columns.length}
                              className="text-xs font-semibold text-center border-l-2 border-r-2 border-gray-300 sticky top-0"
                              style={{
                                backgroundColor:
                                  getGroupBackgroundColor(groupKey),
                                zIndex: 20 - groupIndex,
                              }}
                            >
                              <span className="px-3 py-2 rounded-lg bg-white shadow-md border border-gray-200 text-sm font-bold">
                                {group.title}
                              </span>
                            </TableHead>
                          );
                        },
                      )}
                    </TableRow>
                  )}
                  {/* Fila de headers individuales */}
                  <TableRow className="border-b border-bg+2 bg-bg+1">
                    <TableHead className="text-left p-3 text-sm font-medium text-fg">
                      #
                    </TableHead>
                    {Object.entries(columnGroups).flatMap(([, group]) =>
                      group.columns.map((col) => (
                        <TableHead
                          key={col.index}
                          className="text-left p-3 text-sm font-medium text-fg min-w-[120px] max-w-[200px] whitespace-nowrap"
                        >
                          {col.header}
                        </TableHead>
                      )),
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleRows.map(({ row, index }) => {
                    const rowHasErrors = rowsWithErrors.has(index + 1);
                    const generalErrors = generalErrorsFor(index);
                    const warnings = warningsFor(index);
                    const forceable = warnings.some((warning) =>
                      isForceableWarning(warning.warning),
                    );
                    const forced = forcedRows.includes(index + 1);
                    // A warning not accepted means the row will be skipped (or only update).
                    const rowHasWarning = !rowHasErrors && warnings.length > 0 && !forced;
                    return (
                      <TableRow
                        key={index}
                        className={
                          rowHasErrors ? "bg-red-50" : rowHasWarning ? "bg-amber-50" : "bg-green-50"
                        }
                      >
                        {/* The bar is an inset shadow: TableBody removes the borders of the last row. */}
                        <TableCell
                          className={`text-sm text-fg-muted font-mono ${
                            rowHasErrors
                              ? "shadow-[inset_4px_0_0_0_#ef4444]"
                              : rowHasWarning
                                ? "shadow-[inset_4px_0_0_0_#f59e0b]"
                                : "shadow-[inset_4px_0_0_0_#22c55e]"
                          }`}
                        >
                          <div className="flex flex-col gap-1">
                            <span>{index + 1}</span>
                            {generalErrors.length > 0 && (
                              <ErrorTooltip title="Errores generales:">
                                {generalErrors.map((error, idx) => (
                                  <p key={idx} className="text-sm">
                                    {error.error}
                                  </p>
                                ))}
                              </ErrorTooltip>
                            )}
                            {warnings.length > 0 && (
                              <ErrorTooltip title="Revisa esta fila:" tone="warning">
                                {warnings.map((warning, idx) => (
                                  <p key={idx} className="text-sm">
                                    {importWarningText(warning.warning, warning.detail)}
                                  </p>
                                ))}
                                {forceable && (
                                  <p className="text-sm">
                                    Si no la marcas como &quot;Importar&quot;, la fila se omite.
                                  </p>
                                )}
                              </ErrorTooltip>
                            )}
                            {forceable && (
                              <Checkbox
                                label="Importar"
                                checked={forced}
                                onCheckedChange={(checked) =>
                                  toggleForced(index, checked === true)
                                }
                              />
                            )}
                          </div>
                        </TableCell>
                        {Object.entries(columnGroups).flatMap(([, group]) =>
                          group.columns.map((col) => {
                            const error = errorFor(index, col.dataKey);
                            const raw = String(
                              (row as Record<string, unknown>)[col.dataKey] ?? "",
                            );

                            return (
                              <TableCell
                                key={col.index}
                                className="relative min-w-[120px] max-w-[200px] whitespace-nowrap"
                              >
                                <div className="flex items-center gap-2">
                                  {error && (
                                    <ErrorTooltip title="Error de validación:">
                                      <p className="text-sm">{error.error}</p>
                                    </ErrorTooltip>
                                  )}
                                  <EditableImportCell
                                    value={raw}
                                    displayValue={formatCellValue(raw, col.dataKey)}
                                    hasError={Boolean(error)}
                                    options={optionsFor(col.key)}
                                    onCommit={(value) =>
                                      void commitCell(index, col.key, col.dataKey, value)
                                    }
                                  />
                                </div>
                              </TableCell>
                            );
                          }),
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {previewData.length > 50 && (
              <div className="p-4 text-center text-sm text-fg-muted border-t border-bg+1">
                Mostrando las primeras 50 filas de {previewData.length} filas
                totales
              </div>
            )}
          </CardContent>
        </Card>

        {selectedProfile.id === "candidates" && (
          <div className="flex items-center gap-2">
            <Checkbox
              label="Confiar en los correos de este archivo"
              checked={trustEmails}
              onCheckedChange={(checked) => setTrustEmails(checked === true)}
              data-testid="import-trust-emails"
            />
            <CustomTooltip label="Qué significa confiar en los correos">
              Si marcas esta opción, las personas entran a la app sin escribir un
              código de activación. Es tu responsabilidad que cada correo sea de
              la persona: si hay uno mal escrito que pertenece a otra persona,
              esa persona podría ver lo que registraste y acceder sin autorización.
              Sin marcarla, cada persona confirma con un código que tú le das.
            </CustomTooltip>
          </div>
        )}

        {/* Botón de importación */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            disabled={isImportingData}
            onClick={() =>
              setImportData((prev) => ({
                ...prev,
                currentStep: "columns",
                previewData: [],
                rowErrors: [],
                rowWarnings: [],
                forcedRows: [],
              }))
            }
          >
            <LuArrowLeft className="mr-1 h-4 w-4" />
            Volver a las columnas
          </Button>
          <Button
            onClick={handleImport}
            disabled={isImportingData || hasErrors || overLimit}
            className={`flex items-center gap-2 ${
              isImportingData ? "cursor-not-allowed opacity-75" : ""
            }`}
          >
            {isImportingData ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Importando...
              </>
            ) : (
              <>
                <IoCheckmarkCircle className="h-4 w-4" />
                Confirmar Importación
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Overlay de loading */}
      {isImportingData && (
        <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center z-10">
          <div className="bg-white p-6 rounded-lg shadow-lg flex items-center gap-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="text-gray-700 font-medium" data-testid="import-progress">
              Importando datos...
              {progress && progress.total > 0
                ? ` ${progress.done.toLocaleString("es")} de ${progress.total.toLocaleString("es")} filas`
                : ""}
            </span>
          </div>
        </div>
      )}
    </div>
  );

  // Función para renderizar la pantalla de resultados
  const renderResultsScreen = () => (
    <div className="space-y-6">
      {/* Mostrar resultados de importación */}
      {importResults && (
        <div className="p-6 bg-gray-50 rounded-lg border">
          <h3 className="text-2xl font-bold mb-4 text-gray-900">
            Resultados de la Importación
          </h3>

          <ImportStopNotice result={importResults} />

          {/* Resumen */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="text-center p-3 bg-white rounded border">
              <div className="text-2xl font-bold text-blue-600">
                {importResults.total_processed}
              </div>
              <div className="text-sm text-gray-600">Total Procesados</div>
            </div>
            <div className="text-center p-3 bg-white rounded border">
              <div className="text-2xl font-bold text-green-600">
                {importResults.successful_imports}
              </div>
              <div className="text-sm text-gray-600">Importados</div>
            </div>
            <div className="text-center p-3 bg-white rounded border">
              <div className="text-2xl font-bold text-yellow-600">
                {importResults.duplicates_found}
              </div>
              <div className="text-sm text-gray-600">Duplicados</div>
            </div>
            <div className="text-center p-3 bg-white rounded border">
              <div className="text-2xl font-bold text-red-600">
                {importResults.errors_found}
              </div>
              <div className="text-sm text-gray-600">Errores</div>
            </div>
          </div>

          {/* Detalles de duplicados */}
          {importResults.duplicate_details.length > 0 && (
            <div className="mb-4">
              <h4 className="font-medium text-yellow-800 mb-2">
                Registros con Duplicados (
                {importResults.duplicate_details.length})
              </h4>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {importResults.duplicate_details.map((detail, index) => (
                  <div
                    key={index}
                    className="text-sm p-2 bg-yellow-50 border border-yellow-200 rounded"
                  >
                    <div className="font-medium">Fila {detail.row_number}</div>
                    <div className="text-gray-600">
                      Cliente: {detail.client_name} ({detail.client_action}) |
                      Paciente 1: {detail.patient1_name} (
                      {detail.patient1_action}) | Paciente 2:{" "}
                      {detail.patient2_name} ({detail.patient2_action})
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Detalles de errores */}
          {importResults.error_details.length > 0 && (
            <div className="mb-4">
              <h4 className="font-medium text-red-800 mb-2">
                Registros con Errores ({importResults.error_details.length})
              </h4>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {importResults.error_details.map((detail, index) => (
                  <div
                    key={index}
                    className="text-sm p-2 bg-red-50 border border-red-200 rounded"
                  >
                    <div className="font-medium">Fila {detail.row_number}</div>
                    <div className="text-red-600">{detail.error}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Botones para cerrar resultados */}
          <div className="flex justify-end gap-3">
            <Button
              onClick={() =>
                setImportData(() => ({ ...importInitialState, open: true }))
              }
              variant="outline"
              className="mt-4"
            >
              Importar otros datos
            </Button>
            <Button
              onClick={() => {
                setImportData((prev) => ({ ...prev, importResults: null }));
                // Cerrar el modal de importación
                setImportData((prev) => ({ ...prev, open: false }));
              }}
              className="mt-4"
            >
              Cerrar
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  // Pantalla de resultados de candidatos
  const renderCandidatesResultsScreen = () => {
    const result = importData.candidatesResult;
    if (!result) return null;

    return (
      <div className="space-y-6">
        <div className="p-6 bg-gray-50 rounded-lg border">
          <h3 className="text-2xl font-bold mb-4 text-gray-900">
            Resultados de la Importación
          </h3>

          <ImportStopNotice result={result} />

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="text-center p-3 bg-white rounded border">
              <div className="text-2xl font-bold text-blue-600">
                {result.total_processed}
              </div>
              <div className="text-sm text-gray-600">Total Procesados</div>
            </div>
            <div className="text-center p-3 bg-white rounded border">
              <div className="text-2xl font-bold text-green-600">
                {result.successful_imports}
              </div>
              <div className="text-sm text-gray-600">Importados</div>
            </div>
            <div className="text-center p-3 bg-white rounded border">
              <div className="text-2xl font-bold text-sky-600">
                {result.updated_found}
              </div>
              <div className="text-sm text-gray-600">Actualizados</div>
            </div>
            <div className="text-center p-3 bg-white rounded border">
              <div className="text-2xl font-bold text-yellow-600">
                {result.skipped_found}
              </div>
              <div className="text-sm text-gray-600">Omitidos</div>
            </div>
            <div className="text-center p-3 bg-white rounded border">
              <div className="text-2xl font-bold text-red-600">
                {result.errors_found}
              </div>
              <div className="text-sm text-gray-600">Errores</div>
            </div>
          </div>

          {result.not_added_candidates.length > 0 && (
            <div className="mb-4">
              <h4 className="font-medium text-red-800 mb-2">
                Filas omitidas o con error (
                {result.not_added_candidates.length})
              </h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {result.not_added_candidates.map((candidate, index) => (
                  <div
                    key={index}
                    className="text-sm p-2 bg-red-50 border border-red-200 rounded"
                  >
                    <span className="font-medium">
                      Fila {candidate.row_index} · {candidate.first_name}{" "}
                      {candidate.last_name1} {candidate.last_name2}:
                    </span>{" "}
                    <span className="text-red-600">{candidate.reason}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-wrap justify-end gap-3">
            <Button
              variant="outline"
              onClick={() =>
                setImportData(() => ({ ...importInitialState, open: true }))
              }
            >
              Importar otros datos
            </Button>
            <Button
              onClick={() =>
                setImportData(() => ({ ...importInitialState, open: false }))
              }
            >
              Cerrar
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Return principal que decide qué pantalla mostrar
  if (importData.candidatesResult) return renderCandidatesResultsScreen();

  return currentScreen === "validation"
    ? renderValidationScreen()
    : renderResultsScreen();
}
